// @flow

import * as React from 'react';
import {ButtonLink} from '@stripe-internal/sail/Button';
import {useAccount} from 'docs/data';
import type {TerminalContext} from 'docs/types';
import {defineMessages, FormattedMessage} from 'react-intl';
import {Badge} from '../../core/Badge/Badge';
import {allReaderDetails} from './reader-details';
import type {ReaderDetails} from './reader-details';
import './TerminalReaderComparison.scss';

type CompatibilityData = $PropertyType<TerminalContext, 'compatibility_data'>;
type Shapes = $PropertyType<$Values<CompatibilityData>, 'shapes'>;
type ReaderId = $PropertyType<$ElementType<Shapes, 0>, 'reader'>;

type PropsType = {
  country: string,
  compatibilityData: CompatibilityData,
};

type ReaderTileProps = {
  details: ReaderDetails,
  country: string,
  compatibilityData: CompatibilityData,
};

const messages = defineMessages({
  shopNow: {
    id: 'docsApp.component.TerminalReaderComparison.showNow',
    description: 'Link that takes you to a page to buyer a Terminal reader',
    defaultMessage: 'Shop now',
  },
  contact: {
    id: 'docsApp.component.TerminalReaderComparison.contact',
    description: 'Text that says to contact account manager',
    defaultMessage: 'Contact your Stripe account manager for more details.',
  },
});

const getReaderPrice = (
  details: ReaderDetails,
  country: string,
): string | null => {
  if (details.prices.hasOwnProperty(country)) {
    return details.prices[country];
  } else {
    return details.fallbackPriceCountry != null
      ? details.prices[details.fallbackPriceCountry]
      : null;
  }
};

const ReaderTile = ({details, country, compatibilityData}: ReaderTileProps) => {
  const {account} = useAccount();
  const availableInCountry =
    compatibilityData.hasOwnProperty(country) &&
    compatibilityData[country].shapes.map((s) => s.reader).includes(details.id);
  return (
    <div className="comparison-cell">
      <div className="header">
        <div className="title">
          {details.name}{' '}
          {details.badge && (
            <Badge color={details.badge.color} label={details.badge.label} />
          )}
        </div>
        <div className="description">{details.description}</div>
        <div style={{fontSize: '15px', color: '#13b57c'}}>
          <b>{availableInCountry && getReaderPrice(details, country)}</b>
        </div>
        <br />
        <img src={details.image} alt="" />
      </div>
      <ul className="feature-list">
        {details.features.map((feature) => (
          <li>{feature}</li>
        ))}
      </ul>
      <div className="cta">
        <ButtonLink
          href={`${account.hosts['dashboard.stripe.com']}/terminal/shop`}
          color="blue"
          icon="arrowRight"
          iconPosition="right"
          label={<FormattedMessage {...messages.shopNow} />}
        />
      </div>
    </div>
  );
};

export const TerminalReaderComparison = ({
  country,
  compatibilityData,
}: PropsType) => {
  const createRow = (readerIds: Array<ReaderId>) =>
    readerIds
      .map((readerId) =>
        allReaderDetails.find((reader) => reader.id === readerId),
      )
      .filter(Boolean);

  const rows = [
    createRow(['stripe_m2', 'wisepos_e']),
    createRow(['wisepad3', 'chipper']),
  ];

  return (
    <div className="terminal-reader-comparison">
      {rows.map((row) => (
        <div className="comparison-row">
          {row.map((readerDetails) => (
            <ReaderTile
              details={readerDetails}
              compatibilityData={compatibilityData}
              country={country}
            />
          ))}
        </div>
      ))}
    </div>
  );
};
